import { SetStateAction, SyntheticEvent } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import CursorImage from "assets/customIcons/cursors/cursor-not-allowed.png";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Checkbox from "components/DesignComponents/Checkbox";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import HighlightedOption from "components/HighlightedOption";
import { NOT_ASSIGNED } from "shared/constants/strings";
import { body3Regular } from "shared/styles/typography";

import { useQueryDropdown } from "./hooks";
import { QueryDropdownProps } from "./types";

const QueryDropdown = ({
    resource,
    idType,
    change,
    description,
    textFieldParams,
    exceptions,
    hasOnlyInternalCompanies,
    hasNotAssigned,
    tooltipTitle,
    noOptionsTooltip,
    isRootRequired,
    isCompanyIdRequired,
    hasNoPermission,
    onInitialOptionsLoad,
    getOptionDisabledTooltip,
    ...props
}: QueryDropdownProps) => {
    const { gray700, textDisabled } = colors;

    const { t } = useTranslation();

    const { isLoading, options, query, setQuery } = useQueryDropdown({
        resource,
        idType,
        description,
        exceptions,
        hasOnlyInternalCompanies,
        hasNotAssigned,
        hasUploadedFile: Boolean(noOptionsTooltip?.hasUploadedFile),
        isRootRequired,
        isCompanyIdRequired,
        hasNoPermission,
        onInitialOptionsLoad
    });

    const handleChange = (e: SyntheticEvent, option: any) => {
        setQuery("");
        change(e, option);
    };

    const getOptionLabel = (name: string) => {
        return name === NOT_ASSIGNED ? t("General##not assigned") : name;
    };

    const valueInArray = Array.isArray(props.value)
        ? props.value
        : [props.value];

    const isNoOptions =
        noOptionsTooltip &&
        !isLoading &&
        query.length === 0 &&
        options.length === 0;

    const isDisabled = isNoOptions ?? props.disabled;

    const cursorStyle = isNoOptions
        ? `url(${CursorImage}), default`
        : "default";

    const tooltipTitleText =
        tooltipTitle ?? (isNoOptions ? noOptionsTooltip.title : "");

    return (
        <Autocomplete
            {...props}
            disabled={isDisabled}
            options={[...valueInArray, ...options]}
            filterOptions={() => options}
            value={props.value}
            getOptionLabel={option => getOptionLabel(option.name)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(propsData, option, state) => {
                const { key: _key, ...restProps } = propsData;
                const { id, name, description } = option;

                const isOptionDisabled = propsData["aria-disabled"];
                const disabledTooltipTitle = getOptionDisabledTooltip?.(option);

                return (
                    <TooltipGeneral
                        key={id}
                        title={isOptionDisabled ? disabledTooltipTitle : ""}
                        placement={TOOLTIP_PLACEMENT.Top}
                    >
                        <li>
                            <span {...restProps}>
                                <div
                                    css={css({
                                        display: "flex",
                                        flexDirection: "column"
                                    })}
                                >
                                    <span>
                                        <HighlightedOption
                                            text={getOptionLabel(name)}
                                            query={query}
                                        />
                                    </span>

                                    {description && (
                                        <span
                                            css={[
                                                body3Regular,
                                                {
                                                    color: isOptionDisabled
                                                        ? textDisabled
                                                        : gray700
                                                }
                                            ]}
                                        >
                                            {description}
                                        </span>
                                    )}
                                </div>

                                <div
                                    css={css({
                                        marginLeft: "auto",
                                        alignSelf: "flex-start"
                                    })}
                                >
                                    {props.multiple && (
                                        <Checkbox
                                            checked={state.selected}
                                            disabled={Boolean(isOptionDisabled)}
                                        />
                                    )}
                                </div>
                            </span>
                        </li>
                    </TooltipGeneral>
                );
            }}
            loading={isLoading}
            onChange={(event, option) => handleChange(event, option)}
            tooltipProps={{
                title: tooltipTitleText,
                buttonText: noOptionsTooltip?.buttonText,
                onButtonClick: noOptionsTooltip?.onButtonClick
            }}
            textFieldParams={{
                onChange: (e: { target: { value: SetStateAction<string> } }) =>
                    setQuery(e.target.value),
                onBlur: () => setQuery(""),
                ...(textFieldParams ?? {})
            }}
            css={css({
                "& .MuiAutocomplete-inputRoot": {
                    cursor: cursorStyle,

                    "& .Mui-disabled": {
                        cursor: cursorStyle
                    }
                }
            })}
        />
    );
};

export default QueryDropdown;
